import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useParams } from "react-router-dom";

import { DatasetEditorContext } from "..";

import { ENDPOINTS } from "@/api/application-api";
import { useChannel } from "@/components/Messaging/useChannel";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { WebSocketMessage } from "@/types/websockets";
import { displayErrorToast, displayToast } from "@/utils/toast";

export const useDatasetUploadProgress = () => {
  const { user } = useUser();
  const context = useContext(DatasetEditorContext);
  const queryClient = useQueryClient();
  const { datasetId } = useParams();

  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.DATASET_FILE_PROCESSING_PROGRESS,
    payload => {
      const data = JSON.parse(payload.data);

      if (data.payload.dataset_id !== Number(datasetId)) {
        return;
      }

      if (
        data.payload.type === "file_upload" ||
        (data.payload.type &&
          !data.payload.type.includes("save") &&
          !data.payload.type.includes("copy") &&
          !data.payload.type.includes("export"))
      ) {
        if (context?.processingType !== "upload") {
          context?.setProcessingType?.("upload");
        }

        const { type } = data.payload;
        const progressValue = Math.round(data.payload.progress * 0.3);
        const totalProgress = type === "file_upload" ? 70 + progressValue : data.payload.progress;

        context?.setProcessingProgress?.(totalProgress);
      }
    },
  );

  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.DATASET_FILE_PROCESSING_COMPLETE,
    async (socketMessage: WebSocketMessage) => {
      try {
        const { data: websocketData } = socketMessage;
        if (!websocketData || context?.processingType !== "upload") {
          return;
        }

        const { success, message } = JSON.parse(websocketData);

        if (success) {
          displayToast(message, ToastType.success);
        } else {
          displayErrorToast(message);
        }
      } catch {
        displayErrorToast("Error processing file.");
      } finally {
        await queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
        context?.setProcessingProgress?.(0);
        context?.setProcessingType?.(null);
      }
    },
  );
};
